import { Waypoints } from "lucide-react";
import { Icon as IconifyIcon } from "@iconify/react";
import TaitoraMark from "@/components/brand/TaitoraMark";
import { THEME } from "@/config/theme";
import { useDocumentCtx } from "@/contexts/DocumentCtx";
import { useWorkspaceCtx } from "@/contexts/WorkspaceCtx";
import { isLucideKey, getLucideIcon } from "@/config/projectIcons";
import { getProjectAccent } from "@/utils/projectInitials";

// The workspace's own icon (mock `wsIcon`) — its chosen lucide/iconify/emoji
// glyph, tinted with the workspace accent; falls back to a generic map glyph.
function WorkspaceIcon({ icon, accent }: { icon?: string; accent: string }) {
  if (icon && isLucideKey(icon)) {
    const Lucide = getLucideIcon(icon);
    if (Lucide) return <Lucide size={14} style={{ color: accent }} aria-hidden />;
  }
  if (icon && icon.includes(":") && !isLucideKey(icon)) {
    return <IconifyIcon icon={icon} width={14} height={14} aria-hidden />;
  }
  if (icon && /\p{Emoji}/u.test(icon)) {
    return <span className="text-[13px] leading-none">{icon}</span>;
  }
  return <Waypoints size={14} style={{ color: accent }} aria-hidden />;
}

// Canvas top bar (design-mock TOP BAR): the taitora.ai wordmark + a
// "workspace / map" breadcrumb, floating over the top-left of the canvas. The
// mock's container is transparent — only the mark, wordmark, and crumb show.
export default function CanvasTopBar() {
  const { doc } = useDocumentCtx();
  const { projects } = useWorkspaceCtx();
  const project = doc ? projects.find((p) => p.id === doc.projectId) : undefined;
  const accent = doc ? getProjectAccent(doc.projectId) : "#818cf8";

  return (
    <div className="flex items-center gap-3 select-none font-sidebar">
      <TaitoraMark
        width={26}
        height={24}
        tone="light"
        className="[filter:drop-shadow(0_0_10px_rgba(216,148,60,0.35))]"
      />
      <div className="text-[15px] font-bold tracking-[-0.02em] text-slate-100">
        taitora<span className="opacity-55">.ai</span>
      </div>
      {doc && (
        <div
          data-testid="topbar-breadcrumb"
          className={`flex items-center gap-2 text-[12.5px] ${THEME.sidebar.topBarCrumb}`}
        >
          <span className={`w-px h-[18px] ${THEME.sidebar.topBarDivider}`} aria-hidden />
          <WorkspaceIcon icon={project?.icon} accent={accent} />
          <span className="max-w-[180px] truncate">{doc.projectName}</span>
          <span className="text-zinc-600">/</span>
          <span className={`${THEME.sidebar.topBarCrumbStrong} max-w-[200px] truncate`}>
            {doc.title}
          </span>
        </div>
      )}
    </div>
  );
}
